/* Morgan Stanley makes this available to you under the Apache License,
 * Version 2.0 (the "License"). You may obtain a copy of the License at
 *      http://www.apache.org/licenses/LICENSE-2.0.
 * See the NOTICE file distributed with this work for additional information
 * regarding copyright ownership. Unless required by applicable law or agreed
 * to in writing, software distributed under the License is distributed on an
 * "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express
 * or implied. See the License for the specific language governing permissions
 * and limitations under the License. */

import {
    type AppIdentifier,
    BrowserTypes,
    type Context,
    GetAgentLogLevels,
    ImplementationMetadata,
    Intent,
    LogLevel,
    OpenError,
    ResolveError,
} from '@finos/fdc3';
import { AppDirectoryApplication } from '../app-directory.contracts.js';
import { AppDirectory } from '../app-directory/index.js';
import { ChannelMessageHandler } from '../channel/channel-message-handler.js';
import { ChannelFactory } from '../channel/index.js';
import { HEARTBEAT } from '../constants.js';
import { AddIntentListenerWithContextRequest, IRootPublisher } from '../contracts.internal.js';
import {
    AppIdentifierListenerPair,
    DesktopAgentNext,
    DesktopAgentStrategies,
    EventListenerKey,
    EventListenerLookup,
    FullyQualifiedAppIdentifier,
    IOpenApplicationStrategy,
    ISelectApplicationStrategy,
    RequestMessage,
} from '../contracts.js';
import {
    appInstanceEquals,
    convertToEventListenerIndex,
    createEvent,
    createLogger,
    createResponseMessage,
    decodeUUUrl,
    generateUUID,
    generateUUUrl,
    getHostManifest,
    getImplementationMetadata,
    isContext,
    isDefined,
    isFindInstancesErrors,
    isFullyQualifiedAppIdentifier,
    isOpenApplicationStrategy,
    isOpenError,
    isResponsePayloadError,
    isSelectApplicationStrategy,
    isWCPGoodbye,
    LoggerFunction,
} from '../helpers/index.js';
import { RootMessagePublisher } from '../messaging/index.js';
import { DesktopAgentProxy } from './desktop-agent-proxy.js';
import { FallbackOpenStrategy } from './fallback-open-strategy.js';

type RootDesktopAgentParams = {
    appIdentifier: FullyQualifiedAppIdentifier;
    rootMessagePublisher: RootMessagePublisher;
    directory: AppDirectory;
    channelFactory: ChannelFactory;
    applicationStrategies?: DesktopAgentStrategies[];
    window?: Window; //used for testing FallbackOpenStrategy
    logLevels?: GetAgentLogLevels;
};

/**
 * DesktopAgentImpl is the root agent that all Proxy agents talk to
 * DesktopAgentImpl extends DesktopAgentProxy so that all function calls are handled in the same way - with request and response messages
 * request messages are handled by the onMessage function
 */
export class DesktopAgentImpl extends DesktopAgentProxy implements DesktopAgentNext {
    // Create separate loggers for connection-related and proxy-related messages
    private connectionLog: LoggerFunction;
    private proxyLog;

    private readonly intentListeners: Partial<Record<Intent, AppIdentifierListenerPair[]>> = {};
    //used when raising intents so desktop agent knows when chosen app has added required intentListener
    private readonly intentListenerCallbacks: Map<
        string,
        (source: FullyQualifiedAppIdentifier, intent: string) => void
    >;

    private readonly eventListeners: EventListenerLookup = {};

    public readonly directory: AppDirectory;
    private channelMessageHandler: ChannelMessageHandler;
    private applicationStrategies: DesktopAgentStrategies[];
    private rootMessagePublisher: IRootPublisher;

    // Heartbeat tracking
    private readonly heartbeatTimers: Map<FullyQualifiedAppIdentifier, ReturnType<typeof setInterval>> = new Map();
    private readonly heartbeatRetries: Map<FullyQualifiedAppIdentifier, number> = new Map();
    private readonly heartbeatTimeouts: Map<FullyQualifiedAppIdentifier, ReturnType<typeof setTimeout>> = new Map();
    private readonly connectedProxies: Set<FullyQualifiedAppIdentifier> = new Set();

    constructor(params: RootDesktopAgentParams) {
        super({
            appIdentifier: params.appIdentifier,
            messagingProvider: params.rootMessagePublisher,
            channelFactory: params.channelFactory,
        });

        this.connectionLog = createLogger(DesktopAgentImpl, 'connection', params.logLevels);
        this.proxyLog = createLogger(DesktopAgentImpl, 'proxy', params.logLevels);

        this.rootMessagePublisher = params.rootMessagePublisher;
        params.rootMessagePublisher.requestMessageHandler = this.onRequestMessage.bind(this);
        this.directory = params.directory;
        this.channelMessageHandler = params.channelFactory.createMessageHandler(this.rootMessagePublisher);

        this.intentListenerCallbacks = new Map<string, (source: FullyQualifiedAppIdentifier, intent: string) => void>();

        //if no other strategy works, desktop agent will try the fallback strategy
        this.applicationStrategies = [...(params.applicationStrategies ?? []), new FallbackOpenStrategy(params.window)];
    }

    private async onRequestMessage(
        requestMessage: RequestMessage | BrowserTypes.WebConnectionProtocol6Goodbye,
        sourceApp: FullyQualifiedAppIdentifier,
    ): Promise<void> {
        if (isWCPGoodbye(requestMessage)) {
            this.handleProxyDisconnect(sourceApp);
            return;
        }

        // Start heartbeat monitoring when we receive any message from a proxy
        this.connectionLog('Received message from proxy, ensuring heartbeat is active', LogLevel.DEBUG, {
            type: requestMessage.type,
            sourceApp,
        });
        this.startHeartbeat(sourceApp);

        switch (requestMessage.type) {
            case 'addIntentListenerRequest':
                return this.onAddIntentListenerRequest(requestMessage, sourceApp);
            case 'raiseIntentRequest':
                return this.onRaiseIntentRequest(requestMessage, sourceApp);
            case 'raiseIntentForContextRequest':
                return this.onRaiseIntentForContext(requestMessage, sourceApp);
            case 'intentResultRequest':
                return this.onIntentResultRequest(requestMessage, sourceApp);
            case 'findInstancesRequest':
                return this.onFindInstancesRequest(requestMessage, sourceApp);
            case 'getInfoRequest':
                return this.onGetInfoRequest(requestMessage, sourceApp);
            case 'getAppMetadataRequest':
                return this.onGetAppMetadataRequest(requestMessage, sourceApp);
            case 'findIntentRequest':
                return this.onFindIntentRequest(requestMessage, sourceApp);
            case 'addEventListenerRequest':
                return this.onAddEventListenerRequest(requestMessage, sourceApp);
            case 'findIntentsByContextRequest':
                return this.onFindIntentsByContextRequest(requestMessage, sourceApp);
            case 'eventListenerUnsubscribeRequest':
                return this.onEventListenerUnsubscribeRequest(requestMessage, sourceApp);
            case 'intentListenerUnsubscribeRequest':
                return this.onIntentListenerUnsubscribeRequest(requestMessage, sourceApp);
            case 'openRequest':
                return this.onOpenRequest(requestMessage, sourceApp);
            case 'getUserChannelsRequest':
                return this.channelMessageHandler.onGetUserChannelsRequest(requestMessage, sourceApp);
            case 'getCurrentChannelRequest':
                return this.channelMessageHandler.onGetCurrentChannelRequest(requestMessage, sourceApp);
            case 'joinUserChannelRequest':
                return this.channelMessageHandler.onJoinUserChannelRequest(
                    requestMessage,
                    sourceApp,
                    this.eventListeners,
                );
            case 'leaveCurrentChannelRequest':
                return this.channelMessageHandler.onLeaveCurrentChannelRequest(
                    requestMessage,
                    sourceApp,
                    this.eventListeners,
                );
            case 'createPrivateChannelRequest':
                return this.channelMessageHandler.onCreatePrivateChannelRequest(requestMessage, sourceApp);
            case 'getOrCreateChannelRequest':
                return this.channelMessageHandler.onGetOrCreateChannelRequest(requestMessage, sourceApp);
            case 'addContextListenerRequest':
                return this.channelMessageHandler.onAddContextListenerRequest(requestMessage, sourceApp);
            case 'contextListenerUnsubscribeRequest':
                return this.channelMessageHandler.onContextListenerUnsubscribeRequest(requestMessage, sourceApp);
            case 'broadcastRequest':
                return this.channelMessageHandler.onBroadcastRequest(requestMessage, sourceApp);
            case 'getCurrentContextRequest':
                return this.channelMessageHandler.onGetCurrentContextRequest(requestMessage, sourceApp);
            case 'privateChannelAddEventListenerRequest':
                return this.channelMessageHandler.onPrivateChannelAddEventListenerRequest(requestMessage, sourceApp);
            case 'privateChannelUnsubscribeEventListenerRequest':
                return this.channelMessageHandler.onPrivateChannelUnsubscribeEventListenerRequest(
                    requestMessage,
                    sourceApp,
                );
            case 'privateChannelDisconnectRequest':
                return this.channelMessageHandler.onPrivateChannelDisconnectRequest(requestMessage, sourceApp);
            case 'heartbeatAcknowledgementRequest':
                return this.onHeartbeatAcknowledgementRequest(requestMessage, sourceApp);
        }
    }

    //https://fdc3.finos.org/docs/api/specs/desktopAgentCommunicationProtocol#desktopagent
    private async onRaiseIntentRequest(
        requestMessage: BrowserTypes.RaiseIntentRequest,
        source: FullyQualifiedAppIdentifier,
    ): Promise<void> {
        //check if context argument is invalid
        if (requestMessage.payload.context != null && !isContext(requestMessage.payload.context)) {
            this.rootMessagePublisher.publishResponseMessage(
                createResponseMessage<BrowserTypes.RaiseIntentResponse>(
                    'raiseIntentResponse',
                    { error: ResolveError.MalformedContext },
                    requestMessage.meta.requestUuid,
                    source,
                ),
                source,
            );

            //don't use invalid context
            return;
        }

        let resolveError: any;

        //selects app to resolve intent (may or may not have instanceId)
        const appIdentifier = await this.directory
            .resolveAppForIntent(
                requestMessage.payload.intent,
                requestMessage.payload.context,
                requestMessage.payload.app,
            )
            .catch(err => {
                resolveError = err;
                return undefined;
            });

        const payload: BrowserTypes.RaiseIntentResponsePayload = {};

        if (appIdentifier != null) {
            // If the selected app doesn't have an instanceId, open a new instance
            const fullyQualifiedAppIdentifier = await this.returnOrLaunchAppInstance(
                appIdentifier,
                requestMessage.payload.context,
            );

            //wait for intentListener of correct intent type on chosen app to be added
            await this.awaitIntentListener(fullyQualifiedAppIdentifier, requestMessage.payload.intent).catch(error => {
                resolveError = error;

                this.rootMessagePublisher.publishResponseMessage(
                    createResponseMessage<BrowserTypes.RaiseIntentResponse>(
                        'raiseIntentResponse',
                        { error },
                        requestMessage.meta.requestUuid,
                        source,
                    ),
                    source,
                );
            });

            if (resolveError != null) {
                return;
            }

            //publishes IntentEvent to chosen app to let it know it has been selected to resolve given intent
            this.publishIntentEvent(requestMessage, requestMessage.payload.intent, fullyQualifiedAppIdentifier, source);

            //any results from chosen app resolving intent are sent in a RaiseIntentResultResponse message once intent has been resolved
            payload.intentResolution = {
                source: fullyQualifiedAppIdentifier,
                intent: requestMessage.payload.intent,
            };

            if (isFullyQualifiedAppIdentifier(appIdentifier)) {
                // If the app returned from directory (in turn from App Resolver) is already fully qualified this is an existing application
                // try and select the existing application
                this.tryToSelectApp(fullyQualifiedAppIdentifier);
            }
        } else {
            const error = isFindInstancesErrors(resolveError) ? resolveError : ResolveError.NoAppsFound;

            //no app found to resolve intent
            this.rootMessagePublisher.publishResponseMessage(
                createResponseMessage<BrowserTypes.RaiseIntentResponse>(
                    'raiseIntentResponse',
                    { error },
                    requestMessage.meta.requestUuid,
                    source,
                ),
                source,
            );

            return;
        }

        this.rootMessagePublisher.publishResponseMessage(
            createResponseMessage<BrowserTypes.RaiseIntentResponse>(
                'raiseIntentResponse',
                payload,
                requestMessage.meta.requestUuid,
                source,
            ),
            source,
        );
    }

    private async awaitIntentListener(
        chosenApp: FullyQualifiedAppIdentifier,
        intent: string,
        timeout: number = 15000,
    ): Promise<void> {
        //check if intentListener of correct intent type on chosen app has already been added
        if (
            this.intentListeners[intent] == null ||
            !this.intentListeners[intent]?.some(pair => appInstanceEquals(pair.appIdentifier, chosenApp))
        ) {
            //wait for intentListener of correct intent type on chosen app to be added
            return new Promise<void>((resolve, reject) => {
                const callbackUUID = generateUUID();

                // if the intentListener is not added within a certain time we rturn an IntentDeliveryFailed error
                const timeoutId = setTimeout(() => {
                    this.intentListenerCallbacks.delete(callbackUUID);
                    reject(ResolveError.IntentDeliveryFailed);
                }, timeout);

                this.intentListenerCallbacks.set(callbackUUID, (app, listenerType) => {
                    if (appInstanceEquals(app, chosenApp) && (listenerType == null || listenerType === intent)) {
                        this.intentListenerCallbacks.delete(callbackUUID);
                        clearTimeout(timeoutId);
                        resolve();
                    }
                });
            });
        }
    }

    //https://fdc3.finos.org/docs/api/specs/desktopAgentCommunicationProtocol#desktopagent
    private async onRaiseIntentForContext(
        requestMessage: BrowserTypes.RaiseIntentForContextRequest,
        source: FullyQualifiedAppIdentifier,
    ): Promise<void> {
        //check if context argument is invalid
        if (requestMessage.payload.context != null && !isContext(requestMessage.payload.context)) {
            this.rootMessagePublisher.publishResponseMessage(
                createResponseMessage<BrowserTypes.RaiseIntentForContextResponse>(
                    'raiseIntentForContextResponse',
                    { error: ResolveError.MalformedContext },
                    requestMessage.meta.requestUuid,
                    source,
                ),
                source,
            );

            //don't use invalid context
            return;
        }

        try {
            //selects intent to handle context and app to resolve it
            const resolutionResponse = await this.directory.resolveAppForContext(
                requestMessage.payload.context,
                requestMessage.payload.app,
            );

            if (resolutionResponse == null) {
                //no apps were found to resolve intent
                this.rootMessagePublisher.publishResponseMessage(
                    createResponseMessage<BrowserTypes.RaiseIntentForContextResponse>(
                        'raiseIntentForContextResponse',
                        { error: ResolveError.NoAppsFound },
                        requestMessage.meta.requestUuid,
                        source,
                    ),
                    source,
                );

                return;
            }

            // If the selected app doesn't have an instanceId, open a new instance
            const fullyQualifiedAppIdentifier = await this.returnOrLaunchAppInstance(
                resolutionResponse.app,
                requestMessage.payload.context,
            );

            let resolveError: any;

            //wait for intentListener of correct intent type on chosen app to be added
            await this.awaitIntentListener(fullyQualifiedAppIdentifier, resolutionResponse.intent).catch(error => {
                resolveError = error;

                this.rootMessagePublisher.publishResponseMessage(
                    createResponseMessage<BrowserTypes.RaiseIntentForContextResponse>(
                        'raiseIntentForContextResponse',
                        { error },
                        requestMessage.meta.requestUuid,
                        source,
                    ),
                    source,
                );
            });

            if (resolveError != null) {
                return;
            }

            //publishes IntentEvent to chosen app to let it know it has been selected to resolve chosen intent
            this.publishIntentEvent(requestMessage, resolutionResponse.intent, fullyQualifiedAppIdentifier, source);

            //responds with chosen intent and appIdentifier of the app chosen to resolve it
            const payload = {
                intentResolution: {
                    intent: resolutionResponse.intent,
                    source: fullyQualifiedAppIdentifier,
                },
            };

            if (isFullyQualifiedAppIdentifier(resolutionResponse.app)) {
                // If the app returned from directory (in turn from App Resolver) is already fully qualified this is an existing application
                // try and select the existing application
                this.tryToSelectApp(fullyQualifiedAppIdentifier);
            }

            this.rootMessagePublisher.publishResponseMessage(
                createResponseMessage<BrowserTypes.RaiseIntentForContextResponse>(
                    'raiseIntentForContextResponse',
                    payload,
                    requestMessage.meta.requestUuid,
                    source,
                ),
                source,
            );

            //any results from chosen app resolving intent are sent in a RaiseIntentResultResponse message once intent has been resolved
        } catch (err) {
            const error: BrowserTypes.FindInstancesErrors = isFindInstancesErrors(err) ? err : 'IntentDeliveryFailed';

            this.rootMessagePublisher.publishResponseMessage(
                createResponseMessage<BrowserTypes.RaiseIntentForContextResponse>(
                    'raiseIntentForContextResponse',
                    { error },
                    requestMessage.meta.requestUuid,
                    source,
                ),
                source,
            );
        }
    }

    // https://fdc3.finos.org/docs/api/specs/desktopAgentCommunicationProtocol#addintentlistener
    private async onIntentResultRequest(
        requestMessage: BrowserTypes.IntentResultRequest,
        source: FullyQualifiedAppIdentifier,
    ): Promise<void> {
        this.rootMessagePublisher.publishResponseMessage(
            createResponseMessage<BrowserTypes.IntentResultResponse>(
                'intentResultResponse',
                {},
                requestMessage.meta.requestUuid,
                source,
            ),
            source,
        );

        // We do not want to store the original app that raised the intent so we encode it in the raiseIntentRequestUuid
        // here we decode the string to to obtain the source app and the original raiseIntentRequestUuid to send back to the source app.
        const raiseIntentSource = decodeUUUrl<FullyQualifiedAppIdentifier>(
            requestMessage.payload.raiseIntentRequestUuid,
        );

        if (raiseIntentSource?.payload != null && isFullyQualifiedAppIdentifier(raiseIntentSource.payload)) {
            if (requestMessage.payload.intentResult.channel != null) {
                //if intentResult is PrivateChannel, add receiving app to channel's allowedList
                this.channelMessageHandler.addToPrivateChannelAllowedList(
                    requestMessage.payload.intentResult.channel.id,
                    raiseIntentSource.payload,
                );
            }

            const raiseIntentResultResponse = createResponseMessage<BrowserTypes.RaiseIntentResultResponse>(
                'raiseIntentResultResponse',
                { intentResult: requestMessage.payload.intentResult },
                raiseIntentSource.uuid,
                raiseIntentSource.payload,
            );

            this.rootMessagePublisher.publishResponseMessage(raiseIntentResultResponse, raiseIntentSource.payload);
        }
    }

    /**
     * Sends IntentEvent to app chosen to resolve intent with intent to be resolved and context to be handled
     * Intent is then resolved by chosen app and any result is sent to originating app in RaiseIntentResultResponse message
     */
    private publishIntentEvent(
        requestMessage: BrowserTypes.RaiseIntentRequest | BrowserTypes.RaiseIntentForContextRequest,
        intent: Intent,
        fullyQualifiedApp: FullyQualifiedAppIdentifier,
        originatingApp: FullyQualifiedAppIdentifier,
    ): void {
        // we encode the source app and the original request id in this field to extract them later
        // TODO: we should encrypt this information
        const raiseIntentRequestUuid = generateUUUrl<FullyQualifiedAppIdentifier>(
            originatingApp,
            requestMessage.meta.requestUuid,
        );

        this.rootMessagePublisher.publishEvent(
            createEvent<BrowserTypes.IntentEvent>('intentEvent', {
                intent,
                context: requestMessage.payload.context,
                raiseIntentRequestUuid,
                originatingApp,
            }),
            [fullyQualifiedApp],
        );
    }

    //https://fdc3.finos.org/docs/api/specs/desktopAgentCommunicationProtocol#desktopagent
    /**
     * Registers an app as an intent listener and publishes an AddIntentListenerResponse message
     */
    private async onAddIntentListenerRequest(
        requestMessage: AddIntentListenerWithContextRequest,
        source: FullyQualifiedAppIdentifier,
    ): Promise<void> {
        const listeners =
            this.intentListeners[requestMessage.payload.intent] ??
            (this.intentListeners[requestMessage.payload.intent] = []);

        //fetch context info for app and intent from app directory
        const contexts =
            requestMessage.payload.contextTypes?.map(context => ({
                type: context,
            })) ?? (await this.directory.getContextForAppIntent(source, requestMessage.payload.intent));

        try {
            //this should not occur as error should have been caught by directory.getContextForAppIntent
            await this.directory.registerIntentListener(source, requestMessage.payload.intent, contexts ?? []);
        } catch (error) {
            if (error === ResolveError.TargetInstanceUnavailable) {
                this.rootMessagePublisher.publishResponseMessage(
                    createResponseMessage<BrowserTypes.AddIntentListenerResponse>(
                        'addIntentListenerResponse',
                        { error },
                        requestMessage.meta.requestUuid,
                        source,
                    ),
                    source,
                );
            }
            return;
        }

        const listenerUUID = generateUUID();

        listeners.push({ appIdentifier: source, listenerUUID });

        this.rootMessagePublisher.publishResponseMessage(
            createResponseMessage<BrowserTypes.AddIntentListenerResponse>(
                'addIntentListenerResponse',
                { listenerUUID },
                requestMessage.meta.requestUuid,
                source,
            ),
            source,
        );

        // Defer firing any pending awaitIntentListener callbacks until the next macrotask. In a
        // loopback scenario (where the agent and the proxy that just registered this listener
        // share a JS context) the proxy only registers its `addMessageCallback` for incoming
        // `intentEvent` messages after `await getResponse(addIntentListenerResponse)` has resolved,
        // which takes several microtask ticks. Without this deferral `publishIntentEvent` races
        // ahead of that registration, causing the listener to silently miss the very intent
        // delivery it was registered for.
        setTimeout(() => {
            for (const callback of this.intentListenerCallbacks.values()) {
                callback(source, requestMessage.payload.intent);
            }
        }, 0);
    }

    //https://fdc3.finos.org/docs/api/specs/desktopAgentCommunicationProtocol#desktopagent
    /**
     * Returns array of AppIdentifiers for all available instances of the given app
     */
    private async onFindInstancesRequest(
        requestMessage: BrowserTypes.FindInstancesRequest,
        source: FullyQualifiedAppIdentifier,
    ): Promise<void> {
        const instances = await this.directory.getAppInstances(requestMessage.payload.app.appId);

        if (instances == null) {
            this.rootMessagePublisher.publishResponseMessage(
                createResponseMessage<BrowserTypes.FindInstancesResponse>(
                    'findInstancesResponse',
                    { error: ResolveError.NoAppsFound },
                    requestMessage.meta.requestUuid,
                    source,
                ),
                source,
            );

            return;
        }

        this.rootMessagePublisher.publishResponseMessage(
            createResponseMessage<BrowserTypes.FindInstancesResponse>(
                'findInstancesResponse',
                { appIdentifiers: instances },
                requestMessage.meta.requestUuid,
                source,
            ),
            source,
        );
    }

    //https://fdc3.finos.org/docs/api/specs/desktopAgentCommunicationProtocol#desktopagent
    private async onGetInfoRequest(
        requestMessage: BrowserTypes.GetInfoRequest,
        source: FullyQualifiedAppIdentifier,
    ): Promise<void> {
        try {
            const applicationMetadata = await this.directory.getAppMetadata(source);

            const implementationMetadata: ImplementationMetadata = getImplementationMetadata(
                source,
                applicationMetadata,
            );

            this.rootMessagePublisher.publishResponseMessage(
                createResponseMessage<BrowserTypes.GetInfoResponse>(
                    'getInfoResponse',
                    { implementationMetadata },
                    requestMessage.meta.requestUuid,
                    source,
                ),
                source,
            );
        } catch (error) {
            if (isResponsePayloadError(error)) {
                this.rootMessagePublisher.publishResponseMessage(
                    createResponseMessage<BrowserTypes.GetInfoResponse>(
                        'getInfoResponse',
                        { error },
                        requestMessage.meta.requestUuid,
                        source,
                    ),
                    source,
                );
            } else {
                //error is unexpected
                console.error(error);
            }
        }
    }

    //https://fdc3.finos.org/docs/api/specs/desktopAgentCommunicationProtocol#desktopagent
    private async onGetAppMetadataRequest(
        requestMessage: BrowserTypes.GetAppMetadataRequest,
        source: FullyQualifiedAppIdentifier,
    ): Promise<void> {
        const appMetadata = await this.directory.getAppMetadata(requestMessage.payload.app);

        if (appMetadata == null) {
            //target app is not registered in App Directory
            this.rootMessagePublisher.publishResponseMessage(
                createResponseMessage<BrowserTypes.GetAppMetadataResponse>(
                    'getAppMetadataResponse',
                    { error: ResolveError.TargetAppUnavailable },
                    requestMessage.meta.requestUuid,
                    source,
                ),
                source,
            );

            return;
        }

        this.rootMessagePublisher.publishResponseMessage(
            createResponseMessage<BrowserTypes.GetAppMetadataResponse>(
                'getAppMetadataResponse',
                { appMetadata },
                requestMessage.meta.requestUuid,
                source,
            ),
            source,
        );
    }

    //https://fdc3.finos.org/docs/api/specs/desktopAgentCommunicationProtocol#desktopagent
    /**
     * Return AppIntent containing details of apps which handle given intent
     */
    private async onFindIntentRequest(
        requestMessage: BrowserTypes.FindIntentRequest,
        source: FullyQualifiedAppIdentifier,
    ): Promise<void> {
        //check if context argument is invalid
        if (requestMessage.payload.context != null && !isContext(requestMessage.payload.context)) {
            this.rootMessagePublisher.publishResponseMessage(
                createResponseMessage<BrowserTypes.FindIntentResponse>(
                    'findIntentResponse',
                    { error: ResolveError.MalformedContext },
                    requestMessage.meta.requestUuid,
                    source,
                ),
                source,
            );

            //don't use invalid context
            return;
        }

        const appIntent = await this.directory.getAppIntent(
            requestMessage.payload.intent,
            requestMessage.payload.context,
            requestMessage.payload.resultType,
        );

        if (appIntent.apps.length === 0) {
            //respond with NoAppsFound error message if no registered apps or app instances can resolve given intent
            this.rootMessagePublisher.publishResponseMessage(
                createResponseMessage<BrowserTypes.FindIntentResponse>(
                    'findIntentResponse',
                    { error: ResolveError.NoAppsFound },
                    requestMessage.meta.requestUuid,
                    source,
                ),
                source,
            );

            return;
        }

        this.rootMessagePublisher.publishResponseMessage(
            createResponseMessage<BrowserTypes.FindIntentResponse>(
                'findIntentResponse',
                { appIntent },
                requestMessage.meta.requestUuid,
                source,
            ),
            source,
        );
    }

    //https://fdc3.finos.org/docs/api/specs/desktopAgentCommunicationProtocol#desktopagent
    /**
     * Add an event listener for a given event and app, and respond with the listenerUUID
     */
    private onAddEventListenerRequest(
        requestMessage: BrowserTypes.AddEventListenerRequest,
        source: FullyQualifiedAppIdentifier,
    ): void {
        const eventType = convertToEventListenerIndex(requestMessage.payload.type);
        const listeners = this.eventListeners[eventType] ?? (this.eventListeners[eventType] = []);

        const listenerUUID = generateUUID();

        listeners.push({ appIdentifier: source, listenerUUID });

        this.rootMessagePublisher.publishResponseMessage(
            createResponseMessage<BrowserTypes.AddEventListenerResponse>(
                'addEventListenerResponse',
                { listenerUUID },
                requestMessage.meta.requestUuid,
                source,
            ),
            source,
        );
    }

    //https://fdc3.finos.org/docs/api/specs/desktopAgentCommunicationProtocol#desktopagent
    /**
     * Remove event listener which source app has unsubscribed from
     */
    private onEventListenerUnsubscribeRequest(
        requestMessage: BrowserTypes.EventListenerUnsubscribeRequest,
        source: FullyQualifiedAppIdentifier,
    ): void {
        const eventType = Object.entries(this.eventListeners).find(([_, listenerPairs]) =>
            listenerPairs.some(pair => pair.listenerUUID === requestMessage.payload.listenerUUID),
        )?.[0] as EventListenerKey | undefined;

        if (eventType != null) {
            const listeners = this.eventListeners[eventType];
            const newListeners = listeners?.filter(pair => pair.listenerUUID != requestMessage.payload.listenerUUID);
            this.eventListeners[eventType] = newListeners;
        }

        this.rootMessagePublisher.publishResponseMessage(
            createResponseMessage<BrowserTypes.EventListenerUnsubscribeResponse>(
                'eventListenerUnsubscribeResponse',
                {},
                requestMessage.meta.requestUuid,
                source,
            ),
            source,
        );
    }

    //https://fdc3.finos.org/docs/api/specs/desktopAgentCommunicationProtocol#desktopagent
    /**
     * Remove intent listener which source app has unsubscribed from
     */
    private onIntentListenerUnsubscribeRequest(
        requestMessage: BrowserTypes.IntentListenerUnsubscribeRequest,
        source: FullyQualifiedAppIdentifier,
    ): void {
        const intent = Object.entries(this.intentListeners).find(([_, listenerPairs]) =>
            listenerPairs?.some(pair => pair.listenerUUID === requestMessage.payload.listenerUUID),
        )?.[0];

        if (intent != null) {
            const listeners = this.intentListeners[intent];
            const newListeners = listeners?.filter(pair => pair.listenerUUID != requestMessage.payload.listenerUUID);
            this.intentListeners[intent] = newListeners;
        }

        this.rootMessagePublisher.publishResponseMessage(
            createResponseMessage<BrowserTypes.IntentListenerUnsubscribeResponse>(
                'intentListenerUnsubscribeResponse',
                {},
                requestMessage.meta.requestUuid,
                source,
            ),
            source,
        );
    }

    //https://fdc3.finos.org/docs/api/specs/desktopAgentCommunicationProtocol#desktopagent
    private async onFindIntentsByContextRequest(
        requestMessage: BrowserTypes.FindIntentsByContextRequest,
        source: FullyQualifiedAppIdentifier,
    ): Promise<void> {
        //check if context argument is invalid
        if (requestMessage.payload.context != null && !isContext(requestMessage.payload.context)) {
            this.rootMessagePublisher.publishResponseMessage(
                createResponseMessage<BrowserTypes.FindIntentsByContextResponse>(
                    'findIntentsByContextResponse',
                    { error: ResolveError.MalformedContext },
                    requestMessage.meta.requestUuid,
                    source,
                ),
                source,
            );

            //don't use invalid context
            return;
        }

        const appIntents = await this.directory.getAppIntentsForContext(requestMessage.payload.context);

        if (appIntents.length === 0 || appIntents.find(appIntent => appIntent.apps.length != 0) == null) {
            //responds with error if no intents to handle given context were found, or if no apps which resolve those intents and handle given context were found
            this.rootMessagePublisher.publishResponseMessage(
                createResponseMessage<BrowserTypes.FindIntentsByContextResponse>(
                    'findIntentsByContextResponse',
                    { error: ResolveError.NoAppsFound },
                    requestMessage.meta.requestUuid,
                    source,
                ),
                source,
            );

            return;
        }

        //respond with appIntents found for intents that can handle given context
        this.rootMessagePublisher.publishResponseMessage(
            createResponseMessage<BrowserTypes.FindIntentsByContextResponse>(
                'findIntentsByContextResponse',
                { appIntents },
                requestMessage.meta.requestUuid,
                source,
            ),
            source,
        );
    }

    //https://fdc3.finos.org/docs/api/specs/desktopAgentCommunicationProtocol#desktopagent
    private async onOpenRequest(
        requestMessage: BrowserTypes.OpenRequest,
        source: FullyQualifiedAppIdentifier,
    ): Promise<void> {
        this.proxyLog('OpenRequest', LogLevel.DEBUG, { requestMessage, source });
        //check if context argument is invalid
        if (requestMessage.payload.context != null && !isContext(requestMessage.payload.context)) {
            this.proxyLog('OpenRequest', LogLevel.ERROR, 'MalformedContext', source);
            this.rootMessagePublisher.publishResponseMessage(
                createResponseMessage<BrowserTypes.OpenResponse>(
                    'openResponse',
                    { error: ResolveError.MalformedContext },
                    requestMessage.meta.requestUuid,
                    source,
                ),
                source,
            );

            //don't use invalid context
            return;
        }

        const application = await this.directory.getAppDirectoryApplication(requestMessage.payload.app.appId);

        if (application == null) {
            this.proxyLog('OpenRequest', LogLevel.ERROR, 'AppNotFound', source);
            //app cannot be found in app directory
            this.rootMessagePublisher.publishResponseMessage(
                createResponseMessage<BrowserTypes.OpenResponse>(
                    'openResponse',
                    { error: OpenError.AppNotFound },
                    requestMessage.meta.requestUuid,
                    source,
                ),
                source,
            );
            return;
        }
        this.proxyLog('OpenRequest application resolved', LogLevel.DEBUG, { application, source });

        const strategyCanOpenResults = await Promise.all(
            this.applicationStrategies.filter(isOpenApplicationStrategy).map(async strategy => {
                // if canOpen fails, do not use this strategy
                const canOpen = await this.canStrategyOpenApp(
                    application,
                    strategy,
                    requestMessage.payload.context,
                ).catch(() => false);

                return { canOpen, strategy };
            }),
        );

        const validStrategies: IOpenApplicationStrategy[] = strategyCanOpenResults
            .filter(({ canOpen }) => canOpen)
            .map(({ strategy }) => strategy);

        if (validStrategies.length > 0) {
            const strategy = validStrategies[0];

            this.openAppWithStrategy(strategy, application, requestMessage, source, requestMessage.payload.context);
        } else {
            this.proxyLog('OpenRequest no opening strategies found', LogLevel.ERROR, { source });

            this.rootMessagePublisher.publishResponseMessage(
                createResponseMessage<BrowserTypes.OpenResponse>(
                    'openResponse',
                    { error: OpenError.ErrorOnLaunch },
                    requestMessage.meta.requestUuid,
                    source,
                ),
                source,
            );
        }
    }

    private async openAppWithStrategy(
        strategy: IOpenApplicationStrategy,
        application: AppDirectoryApplication,
        requestMessage: BrowserTypes.OpenRequest,
        source: FullyQualifiedAppIdentifier,
        context?: BrowserTypes.Context,
    ): Promise<void> {
        const { hostManifests, ...noManifests } = application;

        //TODO: allow 15 seconds by default for application to open
        try {
            this.proxyLog('OpenRequest opening application', LogLevel.DEBUG, { application, source, strategy });

            let openError: any;

            // Set as the resolve function of a promise so that when we have created the app Identity we can send it to the strategy
            let resolveAppIdentity: ((appIdentifier: FullyQualifiedAppIdentifier) => void) | undefined;

            const appReadyPromise = new Promise<FullyQualifiedAppIdentifier>(resolve => {
                resolveAppIdentity = resolve;
            });

            const newAppConnectionAttemptUuid = await strategy
                .open({
                    appDirectoryRecord: noManifests,
                    agent: this,
                    manifest: await getHostManifest(hostManifests, strategy.manifestKey).catch(err =>
                        console.error(err),
                    ),
                    context,
                    appReadyPromise,
                })
                .catch(err => {
                    openError = err;
                });

            if (newAppConnectionAttemptUuid == null || openError != null) {
                this.proxyLog('OpenRequest application failed to open', LogLevel.WARN, {
                    application,
                    source,
                    newAppConnectionAttemptUuid,
                    openError,
                });

                this.rootMessagePublisher.publishResponseMessage(
                    createResponseMessage<BrowserTypes.OpenResponse>(
                        'openResponse',
                        { error: openError },
                        requestMessage.meta.requestUuid,
                        source,
                    ),
                    source,
                );

                return;
            }

            this.proxyLog('OpenRequest application opened', LogLevel.DEBUG, {
                application,
                source,
                newAppConnectionAttemptUuid,
            });

            const appIdentifier = await this.rootMessagePublisher.awaitAppIdentity(
                newAppConnectionAttemptUuid,
                application,
            );

            if (resolveAppIdentity != null) {
                resolveAppIdentity(appIdentifier);
            }

            this.proxyLog('OpenRequest appIdentifier resolved', LogLevel.DEBUG, { appIdentifier, source });

            this.rootMessagePublisher.publishResponseMessage(
                createResponseMessage<BrowserTypes.OpenResponse>(
                    'openResponse',
                    { appIdentifier },
                    requestMessage.meta.requestUuid,
                    source,
                ),
                source,
            );

            //pass given context object to opened application via contextListener
            await this.passContextToOpenedApp(requestMessage, source, appIdentifier);
        } catch (err) {
            this.proxyLog('OpenRequest error opening application', LogLevel.ERROR, { application, source, err });
            this.rootMessagePublisher.publishResponseMessage(
                createResponseMessage<BrowserTypes.OpenResponse>(
                    'openResponse',
                    { error: isOpenError(err) ? err : OpenError.ErrorOnLaunch },
                    requestMessage.meta.requestUuid,
                    source,
                ),
                source,
            );
        }
    }

    /**
     * Returns strategy if given strategy can open given application. Rejects Promise otherwise
     */
    private async canStrategyOpenApp(
        application: AppDirectoryApplication,
        strategy: IOpenApplicationStrategy,
        context?: BrowserTypes.Context,
    ): Promise<boolean> {
        const manifest = await getHostManifest(application.hostManifests, strategy.manifestKey).catch(err =>
            console.error(err),
        );

        const { hostManifests, ...appDirectoryRecord } = application;

        const canOpen = await strategy.canOpen({ agent: this, appDirectoryRecord, manifest, context });

        if (canOpen) {
            return true;
        }
        return false;
    }

    private async passContextToOpenedApp(
        requestMessage: BrowserTypes.OpenRequest,
        source: FullyQualifiedAppIdentifier,
        openedApp: FullyQualifiedAppIdentifier,
    ): Promise<void> {
        //TODO: allow 15 seconds by default for application to add necessary contextListeners
        if (requestMessage.payload.context != null) {
            const context = requestMessage.payload.context;
            //await callback listening for creation of contextListener to know when app has added contextListener of correct context type
            await new Promise<void>(resolve => {
                const callbackUUID = generateUUID();
                this.channelMessageHandler.addListenerCallback(callbackUUID, (app, listenerType) => {
                    if (appInstanceEquals(app, openedApp) && (listenerType == null || listenerType === context.type)) {
                        this.channelMessageHandler.removeListenerCallback(callbackUUID);
                        resolve();
                    }
                });
            });
            //publish broadcastEvent with provided context to opened app
            this.publishOpenAppContextBroadcast(context, source, openedApp);
        }
    }

    private publishOpenAppContextBroadcast(
        context: BrowserTypes.Context,
        source: FullyQualifiedAppIdentifier,
        fullyQualifiedAppIdentifier: FullyQualifiedAppIdentifier,
    ): void {
        this.rootMessagePublisher.publishEvent(
            createEvent<BrowserTypes.BroadcastEvent>('broadcastEvent', {
                channelId: null,
                context,
                originatingApp: source,
            }),
            [fullyQualifiedAppIdentifier],
        );
    }

    /**
     * Handle heartbeat acknowledgment from a proxy agent
     * @param requestMessage The heartbeat acknowledgment request
     * @param sourceApp The proxy agent that sent the acknowledgment
     */
    private onHeartbeatAcknowledgementRequest(
        _requestMessage: BrowserTypes.HeartbeatAcknowledgementRequest,
        sourceApp: FullyQualifiedAppIdentifier,
    ): void {
        // Reset retry count when we receive an acknowledgment
        this.heartbeatRetries.set(sourceApp, 0);

        // Clear the timeout timer
        const timeoutTimer = this.heartbeatTimeouts.get(sourceApp);
        if (timeoutTimer) {
            clearTimeout(timeoutTimer);
            this.heartbeatTimeouts.delete(sourceApp);
        }
    }

    /**
     * Start heartbeat monitoring for a proxy
     * @param appId The app ID of the proxy to monitor
     */
    private startHeartbeat(appId: FullyQualifiedAppIdentifier): void {
        // Ignore if the app ID is the same as the current app instance
        // This prevents the heartbeat from being sent to itself
        if (appInstanceEquals(appId, this.appIdentifier)) {
            return;
        }

        if (this.heartbeatTimers.has(appId)) {
            return; // Already monitoring this proxy
        }

        this.connectedProxies.add(appId);
        this.heartbeatRetries.set(appId, 0);

        const timer = setInterval(() => {
            void this.sendHeartbeat(appId);
        }, HEARTBEAT.INTERVAL_MS);

        this.heartbeatTimers.set(appId, timer);

        this.connectionLog('Starting keep-alive for proxy', LogLevel.DEBUG, appId);

        // Send initial appId
        void this.sendHeartbeat(appId);
    }

    /**
     * Send a heartbeat to a proxy and wait for acknowledgment
     * @param appId The app ID of the proxy to send heartbeat to
     */
    private async sendHeartbeat(appId: FullyQualifiedAppIdentifier): Promise<void> {
        const retries = this.heartbeatRetries.get(appId) ?? 0;

        if (retries >= HEARTBEAT.MAX_TRIES) {
            this.handleProxyDisconnect(appId);
            return;
        }

        try {
            // Create a heartbeat event with proper structure
            const heartbeatEvent = createEvent<BrowserTypes.HeartbeatEvent>('heartbeatEvent', {});

            await this.rootMessagePublisher.publishEvent(heartbeatEvent, [appId]);

            // Clear any existing timeout
            const existingTimeout = this.heartbeatTimeouts.get(appId);
            if (existingTimeout) {
                clearTimeout(existingTimeout);
            }

            // Set new timeout for acknowledgment
            const timeout = setTimeout(() => {
                const currentRetries = this.heartbeatRetries.get(appId) ?? 0;
                this.heartbeatRetries.set(appId, currentRetries + 1);
                this.connectionLog(
                    `Heartbeat acknowledgment timeout for proxy. Attempt ${currentRetries + 1}/${HEARTBEAT.MAX_TRIES}`,
                    LogLevel.DEBUG,
                    appId,
                );
            }, HEARTBEAT.TIMEOUT_MS);

            this.heartbeatTimeouts.set(appId, timeout);
        } catch (error) {
            this.connectionLog(`Failed to send heartbeat to proxy ${JSON.stringify(appId)}`, LogLevel.ERROR, error);
            this.handleProxyDisconnect(appId);
        }
    }

    /**
     * Handles cleanup when a proxy disconnects, including timers, listeners, and channel subscriptions.
     * @param appId The app ID of the disconnected proxy
     */
    private handleProxyDisconnect(appId: FullyQualifiedAppIdentifier): void {
        this.connectionLog(`Proxy ${JSON.stringify(appId)} disconnected`, LogLevel.INFO);

        // Clear timers
        this.clearHeartbeatTimers(appId);

        // Clean up intent listeners
        this.cleanupIntentListeners(appId);

        // Clean up event listeners
        this.cleanupEventListeners(appId);

        // Clean up intent listener callbacks
        this.cleanupIntentListenerCallbacks(appId);

        // Clean up channel subscriptions
        this.channelMessageHandler.cleanupDisconnectedProxy(appId);

        this.directory.removeDisconnectedApp(appId);
    }

    /**
     * Clears heartbeat and timeout timers for a given proxy app.
     * @param appId The app ID of the proxy
     */
    private clearHeartbeatTimers(appId: FullyQualifiedAppIdentifier): void {
        const heartbeatTimer = this.heartbeatTimers.get(appId);
        if (heartbeatTimer) {
            clearInterval(heartbeatTimer);
        }

        const timeoutTimer = this.heartbeatTimeouts.get(appId);
        if (timeoutTimer) {
            clearTimeout(timeoutTimer);
        }

        this.heartbeatTimers.delete(appId);
        this.heartbeatRetries.delete(appId);
        this.heartbeatTimeouts.delete(appId);
        this.connectedProxies.delete(appId);
    }

    /**
     * Removes all intent listeners associated with a given proxy app.
     * @param appId The app ID of the proxy
     */
    private cleanupIntentListeners(appId: FullyQualifiedAppIdentifier): void {
        for (const [intent, listeners] of Object.entries(this.intentListeners)) {
            const remainingListeners = listeners?.filter(listener => !appInstanceEquals(listener.appIdentifier, appId));
            if (remainingListeners?.length) {
                this.intentListeners[intent as Intent] = remainingListeners;
            } else {
                delete this.intentListeners[intent as Intent];
            }
        }
    }

    /**
     * Removes all event listeners associated with a given proxy app.
     * @param appId The app ID of the proxy
     */
    private cleanupEventListeners(appId: FullyQualifiedAppIdentifier): void {
        for (const [eventType, listeners] of Object.entries(this.eventListeners)) {
            const remainingListeners = listeners.filter(listener => !appInstanceEquals(listener.appIdentifier, appId));
            if (remainingListeners.length) {
                this.eventListeners[eventType as EventListenerKey] = remainingListeners;
            } else {
                delete this.eventListeners[eventType as EventListenerKey];
            }
        }
    }

    /**
     * Removes all intent listener callbacks associated with a given proxy app.
     * @param appId The app ID of the proxy
     */
    private cleanupIntentListenerCallbacks(appId: FullyQualifiedAppIdentifier): void {
        const callbacksToRemove: string[] = [];
        for (const [key, _] of this.intentListenerCallbacks) {
            const decoded = decodeUUUrl(key);
            if (decoded && decoded.uuid && appInstanceEquals(decoded.payload as FullyQualifiedAppIdentifier, appId)) {
                callbacksToRemove.push(key);
            }
        }
        for (const key of callbacksToRemove) {
            this.intentListenerCallbacks.delete(key);
        }
    }

    /**
     * Uses registered strategies to select an app.
     * An app will only be selected if selection strategies have been provided and at least one returns true for canSelect()
     * This will typically be done if the user raises an intent and selects an existing app.
     * In this case we may want to restore a browser window, bring the window to the front or even switch to a div that contains an iframe that contains the app
     * THe selection mechanism is implemented by the strategy, all we do is look for the first strategy that can select the app then invoke the selectApp function
     * @param appIdentifier
     * @returns
     */
    private async tryToSelectApp(appIdentifier: FullyQualifiedAppIdentifier): Promise<void> {
        const application = await this.directory.getAppDirectoryApplication(appIdentifier.appId);

        const strategyCanSelectResults = (
            await Promise.all(
                this.applicationStrategies.filter(isSelectApplicationStrategy).map(async strategy => {
                    const canSelect = await this.canStrategySelectApp(appIdentifier, strategy, application).catch(
                        () => false,
                    );
                    return canSelect ? strategy : undefined;
                }),
            )
        ).filter(isDefined);

        if (strategyCanSelectResults.length > 0) {
            const strategy = strategyCanSelectResults[0];
            await this.selectAppWithStrategy(appIdentifier, strategy, application);
        }
    }

    private async canStrategySelectApp(
        appIdentifier: FullyQualifiedAppIdentifier,
        strategy: ISelectApplicationStrategy,
        application?: AppDirectoryApplication,
    ): Promise<boolean> {
        const manifest = await getHostManifest(application?.hostManifests, strategy.manifestKey).catch(err =>
            console.error(err),
        );

        return strategy.canSelectApp({
            agent: this,
            appDirectoryRecord: removeHostManifests(application),
            manifest,
            appIdentifier,
        });
    }

    private async selectAppWithStrategy(
        appIdentifier: FullyQualifiedAppIdentifier,
        strategy: ISelectApplicationStrategy,
        application?: AppDirectoryApplication,
    ): Promise<void> {
        await strategy
            .selectApp({
                appIdentifier,
                appDirectoryRecord: removeHostManifests(application),
                agent: this,
                manifest: await getHostManifest(application?.hostManifests, strategy.manifestKey).catch(err =>
                    console.error(err),
                ),
            })
            .catch(err =>
                this.proxyLog(`Error selecting app: ${err}`, LogLevel.DEBUG, {
                    application,
                    appIdentifier,
                }),
            );
    }

    /**
     * Ensures an AppIdentifier is fully qualified (has an instanceId).
     * If the app already has an instanceId, it is returned as-is.
     * If not, a new instance is opened.
     */
    private async returnOrLaunchAppInstance(
        app: AppIdentifier,
        context?: Context,
    ): Promise<FullyQualifiedAppIdentifier> {
        if (isFullyQualifiedAppIdentifier(app)) {
            return app;
        }

        const opened = await this.open(app, context);

        if (isFullyQualifiedAppIdentifier(opened)) {
            return opened;
        }

        return Promise.reject(OpenError.AppNotFound);
    }
}

function removeHostManifests(
    application?: AppDirectoryApplication,
): Omit<AppDirectoryApplication, 'hostManifests'> | undefined {
    if (application == null) {
        return undefined;
    }

    const { hostManifests, ...partialAppDirectory } = application;

    return partialAppDirectory;
}
